import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * A metade "render" da rede que impede a **tela branca** (a outra metade é o
 * script de boot do `index.html` — ver o fim deste comentário).
 *
 * Sem nenhum boundary na árvore, um erro lançado no render de qualquer
 * componente desmonta a raiz inteira: o React remove tudo e sobra um `<div id="root">`
 * vazio — o dashboard "some" e a única pista fica no console, que ninguém tem
 * aberto na hora em que o bug acontece. Foi exatamente o sintoma relatado ao
 * marcar uma questão como anulada.
 *
 * O que este componente troca: em vez de sumir, a tela passa a mostrar **qual**
 * erro foi, **onde** (stack do JS + stack de componentes do React) e um botão
 * para recarregar. Em dev o Vite mapeia o stack para o arquivo/linha do fonte,
 * então o texto que aparece aqui é o suficiente para achar o culpado sem
 * reproduzir de novo.
 *
 * Fica na raiz de propósito: erro em render é sempre fatal para a subárvore, e
 * qualquer tela do admin (não só a de provas) some do mesmo jeito hoje. Um
 * boundary por página seria a mesma coisa repetida N vezes — quando alguma tela
 * precisar de recuperação parcial (manter o menu, recarregar só o miolo), ela
 * põe o seu próprio boundary por dentro deste.
 *
 * **O que ele NÃO pega, e por quê:** o boundary é um componente React, então só
 * existe depois que `main.tsx` carregou. Erro no grafo de módulos acontece
 * ANTES disso — o caso real foi um `import { useCanWriteContent }` de um export
 * que tinha sido removido, que o browser recusa com
 * `SyntaxError: The requested module … does not provide an export named …` e aí
 * nada do app roda (nem este arquivo, nem o CSS). A tela branca continuava
 * branca. Esse buraco é fechado pelo script inline no `index.html`, que é
 * classic script (executa antes do módulo, sobrevive à falha dele) e pinta o
 * erro no `#root` **só se o React não tiver montado nada**. Os dois juntos são
 * a rede: este cobre render, aquele cobre boot.
 */
interface State {
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // O console continua recebendo tudo — é o que o Sentry/observabilidade
    // futura vai capturar, e é onde o stack fica clicável no navegador.
    console.error('[ErrorBoundary]', error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="mx-auto max-w-3xl p-6">
        <div className="space-y-3 rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-500/40 dark:bg-red-500/10">
          <h1 className="text-lg font-semibold text-red-700 dark:text-red-400">
            A tela quebrou ao renderizar
          </h1>
          <p className="text-(--color-text-secondary) text-sm">
            O erro abaixo interrompeu esta página. Copie ou tire um print deste
            texto: ele diz qual arquivo e qual linha causaram o problema.
          </p>
          {/* `select-all` + `overflow-auto`: o texto é para ser copiado inteiro,
              e stack longo não pode empurrar a largura da página. */}
          <pre className="text-(--color-text-primary) max-h-96 select-all overflow-auto rounded border border-red-200 bg-white p-3 text-xs dark:border-red-500/30 dark:bg-black/30">
            {error.name}: {error.message}
            {error.stack ? `\n\n${error.stack}` : ''}
            {componentStack ? `\n\nComponentes:${componentStack}` : ''}
          </pre>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="bg-(--color-accent) rounded-md px-3 py-1.5 text-sm font-medium text-white"
          >
            Recarregar a página
          </button>
        </div>
      </div>
    );
  }
}
